import { useEffect, useState, useSyncExternalStore } from 'react';
import { Users, UserPlus, Briefcase, Plus } from '../../icons';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';

interface TeamsOnboardingProps {
  /** CTA — 팀 셋업 위저드 열기. */
  onStart: () => void;
}

/** 스텝 순환 주기 (ms) — v2.5.3: 하이라이트가 1→2→3 으로 계속 이동. */
const CYCLE_MS = 2800;

/**
 * TeamsOnboarding (v2.5, 2026-06-11 시안 ② 확정) — 팀 0개일 때의 빈 상태 온보딩.
 * 팀 생성 → 멤버 초대 → 프로젝트 추가 3단계 여정을 미리 보여주고 위저드로 유도.
 * v2.5.3: 활성 스텝이 자동 순환(링·도트·진행선 이동) — reduced-motion 시 1단계 고정.
 */
export function TeamsOnboarding({ onStart }: TeamsOnboardingProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setActive((a) => (a + 1) % 3), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const steps = [
    { icon: <Users size={20} />, cls: 'pj-onb-icon--blue', title: tProject('project.onb.step1.title'), desc: tProject('project.onb.step1.desc') },
    { icon: <UserPlus size={20} />, cls: 'pj-onb-icon--green', title: tProject('project.onb.step2.title'), desc: tProject('project.onb.step2.desc') },
    { icon: <Briefcase size={20} />, cls: 'pj-onb-icon--violet', title: tProject('project.onb.step3.title'), desc: tProject('project.onb.step3.desc') },
  ];
  return (
    <section className="pj-onb" aria-label={tProject('project.onb.heading')}>
      <div className="pj-onb-head">
        <h2>{tProject('project.onb.heading')}</h2>
        <p>{tProject('project.onb.sub')}</p>
      </div>
      <div className="pj-onb-progress" aria-hidden="true">
        {steps.map((_, i) => (
          // 도트는 active 까지 채움, 연결선은 통과 구간 채움 + active 직전 구간 흐름
          <span key={`p${i}`} style={{ display: 'contents' }}>
            {i > 0 && (
              <span className={`pj-onb-line${i <= active ? ' pj-onb-line--past' : ''}${i === active + 1 ? ' pj-onb-line--flow' : ''}`} />
            )}
            <span className={`pj-onb-dot${i <= active ? ' pj-onb-dot--on' : ''}${i === active ? ' pj-onb-dot--ping' : ''}`} />
          </span>
        ))}
      </div>
      <div className="pj-onb-steps">
        {steps.map((s, i) => (
          <div key={s.title} className={`pj-onb-step${i === active ? ' pj-onb-step--active' : ''}`}>
            <span className="pj-onb-step-no">{tProject('project.onb.step_label', { n: i + 1 })}</span>
            <span className={`pj-onb-icon ${s.cls}`} aria-hidden="true">{s.icon}</span>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="pj-onb-cta">
        <button type="button" className="pj-button pj-button--primary pj-onb-cta-btn" onClick={onStart}>
          <Plus size={15} />
          {tProject('project.onb.cta')}
        </button>
      </div>
    </section>
  );
}
